import { RestUrl } from "@/commons/constants/RestUrl";
import UrlFormatter from "@/commons/utils/UrlFormatter";
import CommonService from "@/commons/services/CommonService";
import LoginUser from "@/store/LoginUser";
import { HitomeqErrorType } from "@/commons/models/SystemError";

/**
 * 宛先グループ API 操作クラス
 */
export default class SendMessageGroups {
  /** 共通サービス */
  private commonService: CommonService;

  /**
   * ### コンストラクター
   *
   * - 共通サービスが渡されればそれを使用する
   * - 共通サービスが渡されなければ新しいインスタンスを作る
   *
   * @param commonService 共通サービス
   */
  constructor(commonService?: CommonService) {
    this.commonService = commonService ? commonService : new CommonService();
    return;
  }

  /**
   * GET SendMessageGroups
   */
  public async get(userStore: LoginUser, groupId: number): Promise<SendMessageGroupsGetModel> {
    const formatParams: any = {
      facilityId: userStore.facility.id,
      groupId: groupId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.SendMessageGroup, formatParams);
    const response = await this.commonService.executeGet<SendMessageGroupsGetModel>(userStore, requestUrl, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * GET SendMessageGroups
   */
  public async gets(userStore: LoginUser, queryParams?: { startIndex: number; endIndex: number; sortKey: string; order: string }): Promise<Array<SendMessageGroupsGetModel>> {
    const formatParams: any = {
      facilityId: userStore.facility.id
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.SendMessageGroups, formatParams);
    const params: any = queryParams ? queryParams : {};
    const response = await this.commonService.executeGet<Array<SendMessageGroupsGetModel>>(userStore, requestUrl, this.constructor.name, params, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * GET SendMessageGroupsCount
   */
  public async getCount(userStore: LoginUser): Promise<number> {
    const formatParams: any = {
      facilityId: userStore.facility.id
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.SendMessageGroups, formatParams);

    // countの場合は末尾に「count」をつける
    const response = await this.commonService.executeGet<{ count: number }>(userStore, `${requestUrl}/count`, this.constructor.name, null, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data.count;
  }

  /**
   * POST SendMessageGroups
   */
  public async post(userStore: LoginUser, requestBody: SendMessageGroupsPostModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.facility.id
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.SendMessageGroups, formatParams);
    const response = await this.commonService.executePost<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * PATCH SendMessageGroups
   */
  public async patch(userStore: LoginUser, groupId: number, requestBody: SendMessageGroupsPatchModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.facility.id,
      groupId: groupId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.SendMessageGroup, formatParams);
    const response = await this.commonService.executePatch<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * DELETE SendMessageGroups
   */
  public async delete(userStore: LoginUser, groupId: number): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.facility.id,
      groupId: groupId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.SendMessageGroup, formatParams);
    const response = await this.commonService.executeDelete<any>(userStore, requestUrl, this.constructor.name, null, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }
}

export interface SendMessageGroupsGetModel {
  facilityId: number;
  sendMessageGroupsSortKey: string;
  id: number;
  type: GroupType;
  name: string;
  members: Array<string>;
  feleteFlag: boolean;
  created: number;
  updated: number;
}

export enum GroupType {
  // 画面バリデーションで使用するので、初期値としてい使用する場合は空文字を格納する
  UNKNOWN = "",
  FAMILY = 0,
  OTHER_USER = 1,
  RESIDENT_GROUP = 2,
  OTHER_USER_GROUP = 3
}

export interface SendMessageGroupsPostModel {
  name: string;
  type: GroupType;
  members: Array<string>;
}

export interface SendMessageGroupsPatchModel {
  name: string;
  type: GroupType;
  members: Array<string>;
  updated: number;
}
